import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { useTranslation } from "@/i18n/LanguageContext";
import { Loader2, ThumbsUp, ThumbsDown } from "lucide-react";
import { useLocation } from "wouter";

interface PuzzleHint {
  id: number;
  content: string;
  price: number;
  customPuzzleId: number;
  createdAt: string;
  updatedAt: string;
  purchased?: boolean;
  likesCount?: number;
  dislikesCount?: number;
  userVote?: number;
}

interface PuzzleHintsTabProps {
  puzzle: {
    id: number;
    userId: number;
    solved: boolean;
  };
  user: {
    id: number;
    email: string;
    displayName?: string;
    avatarUrl?: string;
    cacatais: number;
  } | undefined;
  hints: PuzzleHint[] | undefined;
  processedHints: PuzzleHint[] | undefined;
  shouldShowAllHints: boolean;
  isHintsLoading: boolean;
  isPurchasedHintsLoading: boolean;
  voteHintMutationIsPending: boolean;
  purchaseHintMutationIsPending: boolean;
  handleVoteHint: (hintId: number, voteValue: number) => void;
  handlePurchaseHint: (hintId: number, price: number) => void;
}

export function PuzzleHintsTab({
  puzzle,
  user,
  hints,
  processedHints,
  shouldShowAllHints,
  isHintsLoading,
  isPurchasedHintsLoading,
  voteHintMutationIsPending,
  purchaseHintMutationIsPending,
  handleVoteHint,
  handlePurchaseHint
}: PuzzleHintsTabProps) {
  const { t } = useTranslation();
  const [, setLocation] = useLocation();

  return (
    <div className="space-y-6">
      {/* Display hints section for puzzle owner */}
      {user && user.id === puzzle.userId && (
        <div>
          <h3 className="text-lg font-semibold text-white mb-2">{t("desafios_cacatais.existing_hints", { fallback: "Dicas Existentes" })}</h3>
          {isHintsLoading ? (
            <div className="text-center py-4">
              <Loader2 className="h-6 w-6 animate-spin mx-auto" />
            </div>
          ) : hints && hints.length > 0 ? (
            <div className="space-y-3">
              {hints.map((hint, index) => (
                <div key={hint.id} className="bg-zinc-800 rounded-md p-3">
                  <div className="flex justify-between items-center mb-2">
                    <span className="text-sm font-medium text-zinc-300">
                      {t("desafios_cacatais.hint_title", { fallback: "Dica" })} {index + 1}
                    </span>
                    <Badge variant="outline">
                      {t("desafios_cacatais.hint_price", { 
                        price: parseFloat(hint.price.toString()).toLocaleString(undefined, { 
                          minimumFractionDigits: 2, 
                          maximumFractionDigits: 2 
                        }),
                        fallback: `${parseFloat(hint.price.toString()).toLocaleString(undefined, { 
                          minimumFractionDigits: 2, 
                          maximumFractionDigits: 2 
                        })} caçatais`
                      })}
                    </Badge>
                  </div>
                  <p className="text-zinc-300 whitespace-pre-line">{hint.content}</p>
                  
                  {/* Voting UI for puzzle owner's hints */}
                  <div className="flex items-center justify-between mt-4 pt-3 border-t border-zinc-600">
                    <div className="flex items-center space-x-3">
                      <div className="flex items-center space-x-1">
                        <ThumbsUp className="h-4 w-4 text-zinc-300" />
                        <span className="text-sm text-zinc-300">{hint.likesCount || 0}</span>
                      </div>
                      <div className="flex items-center space-x-1">
                        <ThumbsDown className="h-4 w-4 text-zinc-300" />
                        <span className="text-sm text-zinc-300">{hint.dislikesCount || 0}</span>
                      </div>
                    </div>
                    <div>
                      <span className="text-xs text-zinc-400">
                        {t("desafios_cacatais.hints.vote_stats", { fallback: "Estatísticas de votos" })}
                      </span>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <p className="text-zinc-400 italic">
              {t("desafios_cacatais.no_hints", { fallback: "Nenhuma dica adicionada ainda." })}
            </p>
          )}
          
          <div className="flex flex-wrap justify-end gap-2 sm:gap-3 pt-4">
            <Button 
              variant="outline"
              onClick={() => setLocation(`/puzzles/custom/${puzzle.id}/hints`)}
            >
              {t("desafios_cacatais.sell_hints", { fallback: "Vender Dicas" })}
            </Button>
          </div>
        </div>
      )}

      {/* Display hints section for non-owners */}
      {user && user.id !== puzzle.userId && (
        <div>
          <h3 className="text-lg font-semibold text-white mb-2">{t("desafios_cacatais.available_hints", { fallback: "Dicas Disponíveis" })}</h3>
          {isHintsLoading || isPurchasedHintsLoading ? (
            <div className="text-center py-4">
              <Loader2 className="h-6 w-6 animate-spin mx-auto" />
            </div>
          ) : processedHints && processedHints.length > 0 ? (
            <div className="space-y-3">
              {processedHints.map((hint, index) => (
                <div key={hint.id} className="bg-zinc-800 rounded-md p-3">
                  <div className="flex justify-between items-center mb-2">
                    <span className="text-sm font-medium text-zinc-300">
                      {t("desafios_cacatais.hint_title", { fallback: "Dica" })} {index + 1}
                    </span>
                    <Badge variant="outline">
                      {t("desafios_cacatais.hint_price", { 
                        price: parseFloat(hint.price.toString()).toLocaleString(undefined, { 
                          minimumFractionDigits: 2, 
                          maximumFractionDigits: 2 
                        }),
                        fallback: `${parseFloat(hint.price.toString()).toLocaleString(undefined, { 
                          minimumFractionDigits: 2, 
                          maximumFractionDigits: 2 
                        })} caçatais`
                      })}
                    </Badge>
                  </div>
                  
                  {hint.purchased || shouldShowAllHints ? (
                    // Show hint content if purchased or puzzle is solved
                    <div className={`p-3 bg-zinc-700 rounded-md border-l-2 ${hint.purchased ? 'border-green-500' : 'border-blue-500'}`}>
                      {shouldShowAllHints && !hint.purchased && (
                        <div className="mb-2">
                          <Badge variant="secondary">
                            {t("desafios_cacatais.hints.revealed", { fallback: "Revelado após solução" })}
                          </Badge>
                        </div>
                      )}
                      <p className="text-zinc-300 whitespace-pre-line">{hint.content}</p>
                      
                      {/* Voting UI */}
                      <div className="flex items-center justify-between mt-4 pt-3 border-t border-zinc-600">
                        <div className="flex items-center space-x-3">
                          <div className="flex items-center space-x-1">
                            <Button 
                              variant={hint.userVote === 1 ? "default" : "outline"} 
                              size="sm"
                              onClick={() => handleVoteHint(hint.id, 1)}
                              disabled={voteHintMutationIsPending}
                              className="px-2"
                            >
                              <ThumbsUp className="h-4 w-4" />
                            </Button>
                            <span className="text-sm text-zinc-300">{hint.likesCount || 0}</span>
                          </div>
                          <div className="flex items-center space-x-1">
                            <Button 
                              variant={hint.userVote === -1 ? "destructive" : "outline"} 
                              size="sm"
                              onClick={() => handleVoteHint(hint.id, -1)}
                              disabled={voteHintMutationIsPending}
                              className="px-2"
                            >
                              <ThumbsDown className="h-4 w-4" />
                            </Button>
                            <span className="text-sm text-zinc-300">{hint.dislikesCount || 0}</span>
                          </div>
                        </div>
                        <div>
                          <span className="text-xs text-zinc-400">
                            {t("desafios_cacatais.hints.vote_help", { fallback: "Vote na utilidade da dica" })}
                          </span>
                        </div>
                      </div>
                    </div>
                  ) : (
                    // Show purchase button if not purchased and puzzle is not solved
                    <div className="flex flex-col gap-3">
                      <div className="text-center py-2">
                        <Button 
                          variant="outline" 
                          size="sm"
                          onClick={() => handlePurchaseHint(hint.id, hint.price)}
                          disabled={purchaseHintMutationIsPending}
                        >
                          {purchaseHintMutationIsPending ? (
                            <>
                              <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                              {t("desafios_cacatais.hints.purchasing", { fallback: "Comprando..." })}
                            </>
                          ) : (
                            t("desafios_cacatais.hints.purchase", { fallback: "Comprar Dica" })
                          )}
                        </Button>
                      </div>
                      
                      {/* Voting UI for unpurchased hints */}
                      <div className="flex items-center justify-between pt-3 border-t border-zinc-600">
                        <div className="flex items-center space-x-3">
                          <div className="flex items-center space-x-1">
                            <Button 
                              variant={hint.userVote === 1 ? "default" : "outline"} 
                              size="sm"
                              onClick={() => handleVoteHint(hint.id, 1)}
                              disabled={voteHintMutationIsPending}
                              className="px-2"
                            >
                              <ThumbsUp className="h-4 w-4" />
                            </Button>
                            <span className="text-sm text-zinc-300">{hint.likesCount || 0}</span>
                          </div>
                          <div className="flex items-center space-x-1">
                            <Button 
                              variant={hint.userVote === -1 ? "destructive" : "outline"} 
                              size="sm"
                              onClick={() => handleVoteHint(hint.id, -1)}
                              disabled={voteHintMutationIsPending}
                              className="px-2"
                            >
                              <ThumbsDown className="h-4 w-4" />
                            </Button>
                            <span className="text-sm text-zinc-300">{hint.dislikesCount || 0}</span>
                          </div>
                        </div>
                        <div>
                          <span className="text-xs text-zinc-400">
                            {t("desafios_cacatais.hints.vote_help", { fallback: "Vote na utilidade da dica" })}
                          </span>
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              ))}
            </div>
          ) : (
            <p className="text-zinc-400 italic">
              {t("desafios_cacatais.no_hints", { fallback: "Nenhuma dica disponível." })}
            </p>
          )}
        </div>
      )}
      
      {!user && (
        <div className="text-center py-4">
          <p className="text-zinc-400 mb-4">
            {t("desafios_cacatais.hints.login_required", { fallback: "Faça login para ver as dicas disponíveis." })}
          </p>
          <Button 
            variant="outline"
            onClick={() => setLocation("/auth")}
          >
            {t("auth.login", { fallback: "Entrar" })}
          </Button>
        </div>
      )}
    </div>
  );
}
